import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import {
  androidBackActionFor,
  isBlockingGateShown,
  screenBackFor,
  topLayerClose,
} from 'Utils/androidBackButton';
import logger from 'Utils/Logger';

/**
 * Takes over Android's Back for the app (see `Utils/androidBackButton`). Once a `backButton`
 * listener is registered the WebView no longer goes back by itself, so every case is handled
 * here. Web and iOS register nothing.
 */
export const useAndroidBackButton = (): void => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (Capacitor.getPlatform() !== 'android') return;
    let removed = false;
    let handle: PluginListenerHandle | undefined;
    CapApp.addListener('backButton', ({ canGoBack }) => {
      const screenBack = screenBackFor(pathnameRef.current);
      const closeTopLayer = topLayerClose();
      const action = androidBackActionFor({
        pathname: pathnameRef.current,
        canGoBack,
        blockingGateShown: isBlockingGateShown(),
        screenHandlesBack: screenBack !== undefined,
        dismissibleLayerOpen: closeTopLayer !== undefined,
      });
      if (action === 'close-layer') {
        closeTopLayer?.();
      } else if (action === 'screen-back') {
        screenBack?.();
      } else if (action === 'minimize') {
        CapApp.minimizeApp().catch((error: unknown) =>
          logger.warn('[AndroidBack] minimizeApp failed', error),
        );
      } else if (action === 'go-to-roadmap') {
        navigateRef.current('/roadmap');
      } else {
        navigateRef.current(-1);
      }
    })
      .then((listenerHandle) => {
        if (removed) void listenerHandle.remove();
        else handle = listenerHandle;
      })
      .catch((error: unknown) => logger.warn('[AndroidBack] backButton listener failed', error));
    return () => {
      removed = true;
      void handle?.remove();
    };
  }, []);
};
